import { useEffect, useState } from "react";

import { Edit as EditIcon } from "@mui/icons-material";
import { Stack } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { useGetUserCourseTemplateQuery } from "api/hooks/useGetUserCourseTemplate";
import { ExerciseLibraryItem } from "routes/patients/PatientProfile/components/UserCourse/components/ExerciseLibraryItem";
import { LessonLibraryItem } from "routes/patients/PatientProfile/components/UserCourse/components/LessonLibraryItem";
import { JointImage } from "routes/patients/PatientProfile/components/UserCourse/components/Templates";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { translateTemplateKey } from "utils/translations";

import { PageContainer } from "../../components/PageContainer";
import { CardContainer, ContentContainer, headerFont } from "../../components/styles";

import { ConvertTemplateModal } from "./ConvertTemplateModal";

export const Template: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const templateId = id ? parseInt(id, 10) : -1;
  const [showModal, setShowModal] = useState(false);

  const { data, isError, isLoading } = useGetUserCourseTemplateQuery(
    { templateId },
    {
      enabled: templateId > -1,
    }
  );

  useEffect(() => {
    if (data?.standardized === false) {
      setShowModal(true);
    }
  }, [data]);

  const name = data?.key ? translateTemplateKey(data.key) : data?.name || id;
  const image = data?.key ? <JointImage id={data.key} /> : undefined;

  return (
    <PageContainer isError={isError} isLoading={isLoading}>
      <ContentContainer>
        <Header>
          {image && <ImageContainer>{image}</ImageContainer>}
          <h2>{name}</h2>
          {!data?.standardized && (
            <Stack direction="row" spacing={2} marginLeft="auto">
              <BaseButton
                text={t("library.convert_template")}
                onClick={() => setShowModal(true)}
                style={{ flexGrow: 0 }}
              />
              <IconButton
                onClick={() => navigate(`/library/your-library/templates/${id}/edit`)}
                size="large"
                style={{ marginLeft: "auto" }}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          )}
        </Header>

        <Section>
          <span>
            {t("usercourse.exercise_per_day")} {data?.max_exercises_per_day}
          </span>
          <span>
            {t("usercourse.function_test")} {data?.functional_test ? t("buttons.yes") : t("buttons.no")}
          </span>
          <span>
            {t("usercourse.health_form_type")} {t(`usercourse.healthformtype.${data?.health_form_type || "generic"}`)}
          </span>
        </Section>

        <Section>
          <SectionHeader>{t("common.exercises")}</SectionHeader>
          {data?.exercises?.length ? (
            <CardContainer>
              {data.exercises.map(exercise => (
                <ExerciseLibraryItem
                  key={`exercise_${exercise.id}`}
                  item={{
                    ...exercise,
                    exercise_id: exercise.id,
                  }}
                  checked={false}
                  showAddButton={false}
                  onClick={() => navigate(`../exercises/${exercise.id}`)}
                />
              ))}
            </CardContainer>
          ) : (
            <Notification variant="warning">{t("library.no_activities_added", { category: "exercises" })}</Notification>
          )}
        </Section>

        <Section>
          <SectionHeader>{t("common.lessons")}</SectionHeader>
          {data?.lessons?.length ? (
            <CardContainer>
              {data.lessons.map(lesson => (
                <LessonLibraryItem
                  key={`lesson_${lesson.id}`}
                  item={lesson}
                  checked={false}
                  onClick={() => navigate(`../lessons/${lesson.id}`)}
                />
              ))}
            </CardContainer>
          ) : (
            <Notification variant="warning">{t("library.no_activities_added", { category: "lessons" })}</Notification>
          )}
        </Section>
      </ContentContainer>

      {showModal && <ConvertTemplateModal templateID={templateId} onClose={() => setShowModal(false)} />}
    </PageContainer>
  );
};

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 24px;

  & > h2 {
    ${headerFont}
  }
`;

const ImageContainer = styled.div`
  width: 60px;
  height: 60px;

  & > svg {
    height: 100%;
    width: 100%;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.h3`
  margin: 0;
`;
